import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Search, RefreshCw, Loader } from 'lucide-react';

const HotSearchWidget = ({ size = '2x2', widgetScale = 1 }) => {
  const [hotList, setHotList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [allPlatformsData, setAllPlatformsData] = useState({});
  const [platformKeys, setPlatformKeys] = useState([]);
  const [currentPlatformIndex, setCurrentPlatformIndex] = useState(0);

  const [wStr, hStr] = size.split('x');
  const w = parseInt(wStr, 10) || 2;
  const h = parseInt(hStr, 10) || 2;
  
  const showHotIndex = w >= 4 || h >= 4 || (w === 4 && h === 2) || size === '4x2' || size === '2x4';

  const fetchList = async () => {
    setLoading(true);
    try {
      const res = await fetch('/api/v1/hotsearch/list').then(r => r.json());
      if (res.code === 200 && res.data) {
        const allData = typeof res.data === 'string' ? JSON.parse(res.data) : res.data;
        setAllPlatformsData(allData);
        
        let keys = Object.keys(allData);
        if (keys.length > 0) {
          // Prioritize popular platforms
          const preferred = ['百度', '微博', '知乎', '哔哩哔哩', 'B站', '抖音', '头条'];
          keys.sort((a, b) => {
             const idxA = preferred.findIndex(p => a.includes(p));
             const idxB = preferred.findIndex(p => b.includes(p));
             if (idxA !== -1 && idxB !== -1) return idxA - idxB;
             if (idxA !== -1) return -1;
             if (idxB !== -1) return 1;
             return 0;
          });
          setPlatformKeys(keys);
          setCurrentPlatformIndex(0);
          setHotList(allData[keys[0]] || []);
        }
      }
    } catch (e) {
      console.error('Failed to fetch hot search list', e);
      setHotList([]);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = (e) => {
    e.stopPropagation();
    if (platformKeys.length > 1) {
      const nextIndex = (currentPlatformIndex + 1) % platformKeys.length;
      setCurrentPlatformIndex(nextIndex);
      setHotList(allPlatformsData[platformKeys[nextIndex]] || []);
    } else {
      fetchList();
    }
  };

  useEffect(() => {
    fetchList();
  }, []);

  // 1x1 size: Icon only
  if (w === 1 && h === 1) {
    return (
      <div 
        title="热搜榜"
        style={{ 
          width: '100%', height: '100%', 
          display: 'flex', alignItems: 'center', justifyContent: 'center', 
          cursor: 'pointer'
        }}
      >
        <Search size={32 * widgetScale} color="#ff4d4f" />
      </div>
    );
  }

  // 2x1 size: Icon + Text
  if (w === 2 && h === 1) {
    return (
      <div 
        title="热搜榜"
        style={{ 
          width: '100%', height: '100%', 
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: `${16 * widgetScale}px`, 
          cursor: 'pointer'
        }}
      >
        <Search size={28 * widgetScale} color="#ff4d4f" />
        <div style={{ display: 'flex', flexDirection: 'column', gap: `${6 * widgetScale}px` }}>
           <div style={{ width: `${40 * widgetScale}px`, height: `${4 * widgetScale}px`, background: 'rgba(255,255,255,0.4)', borderRadius: '2px' }}></div>
           <div style={{ width: `${24 * widgetScale}px`, height: `${4 * widgetScale}px`, background: 'rgba(255,255,255,0.2)', borderRadius: '2px' }}></div>
           <div style={{ width: `${32 * widgetScale}px`, height: `${4 * widgetScale}px`, background: 'rgba(255,255,255,0.2)', borderRadius: '2px' }}></div>
        </div>
      </div>
    );
  }

  // 2x2 and 4x2 sizes: List view
  if (hotList.length === 0) {
    return (
      <div style={{ 
        width: '100%', height: '100%', 
        display: 'flex', justifyContent: 'center', alignItems: 'center',
        color: '#fff'
      }}>
        <Loader className="spin" size={24 * widgetScale} style={{ animation: 'spin 1s linear infinite' }} />
      </div>
    );
  }

  return (
    <div style={{ 
      width: '100%', height: '100%', 
      display: 'flex', flexDirection: 'column', 
      color: '#fff'
    }}>
      <div style={{ 
        padding: `${10 * widgetScale}px ${16 * widgetScale}px ${4 * widgetScale}px`, 
        display: 'flex', alignItems: 'center', justifyContent: 'space-between' 
      }}>
        <div style={{ display: 'flex', alignItems: 'center', paddingLeft: `${6 * widgetScale}px` }}>
          <span style={{ fontWeight: 600, fontSize: `${0.95 * widgetScale}rem`, opacity: 0.95, letterSpacing: '0.5px' }}>
            {platformKeys.length > 0 
              ? (platformKeys[currentPlatformIndex].endsWith('热搜') || platformKeys[currentPlatformIndex].endsWith('热榜') 
                  ? platformKeys[currentPlatformIndex] 
                  : `${platformKeys[currentPlatformIndex]}热搜`) 
              : '热搜榜'}
          </span>
        </div>
        <div 
          onClick={handleRefresh}
          style={{ cursor: 'pointer', opacity: 0.5, display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'opacity 0.2s' }}
          onMouseEnter={(e) => e.currentTarget.style.opacity = '1'}
          onMouseLeave={(e) => e.currentTarget.style.opacity = '0.5'}
          title="刷新"
        >
          <RefreshCw size={14 * widgetScale} className={loading ? "spin-animation" : ""} />
        </div>
      </div>

      <div style={{ flex: 1, overflowY: 'hidden', padding: `0 ${16 * widgetScale}px ${10 * widgetScale}px`, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
        {hotList.slice(0, h === 2 ? 5 : 8).map((item, index) => {
          const numColor = index === 0 ? '#ff4d4f' : index === 1 ? '#fa541c' : index === 2 ? '#fa8c16' : 'rgba(255,255,255,0.4)';
            return (
              <motion.a 
                key={item.id || index}
                href={item.url}
                target="_blank"
                rel="noreferrer"
                style={{ 
                  display: 'flex', alignItems: 'center', gap: `${6 * widgetScale}px`, 
                  textDecoration: 'none', color: '#fff', 
                  padding: `${3 * widgetScale}px 0`, 
                  borderRadius: '6px', 
                  transition: 'background 0.2s',
                  position: 'relative'
                }}
                whileHover={{ background: 'rgba(255, 255, 255, 0.05)', paddingLeft: `${4 * widgetScale}px`, paddingRight: `${4 * widgetScale}px`, marginLeft: `-${4 * widgetScale}px`, marginRight: `-${4 * widgetScale}px` }}
                onPointerDown={(e) => e.stopPropagation()} 
              >
                <span style={{ 
                  color: numColor, 
                  width: `${18 * widgetScale}px`, 
                  textAlign: 'center', 
                  fontSize: `${0.95 * widgetScale}rem`, 
                  fontWeight: 600, 
                  flexShrink: 0,
                  fontFamily: 'system-ui, -apple-system, sans-serif'
                }}>
                  {index + 1}
                </span>
                <div style={{ flex: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontSize: `${0.85 * widgetScale}rem`, opacity: 0.9 }}>
                  {item.title}
                </div>
                {showHotIndex && item.hotIndex && (
                  <div style={{ fontSize: `${0.75 * widgetScale}rem`, opacity: 0.4, flexShrink: 0 }}>
                    {item.hotIndex}
                  </div>
                )}
              </motion.a>
            );
          })
        }
      </div>
    </div>
  );
};

export default HotSearchWidget;
